<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Bubble from 'dashboard/components-next/message/bubbles/Bubble.vue';
import {
  MESSAGE_VARIANTS,
  ORIENTATION,
} from 'dashboard/components-next/message/constants';
import InternalChatMessageActions from './InternalChatMessageActions.vue';
import InternalChatMessageContent from './InternalChatMessageContent.vue';
import InternalChatEditForm from './InternalChatEditForm.vue';

defineProps({
  message: { type: Object, required: true },
  saveMessage: { type: Function, required: true },
  members: { type: Object, default: () => ({ users: [], teams: [] }) },
  canMentionAll: { type: Boolean, default: false },
  currentUserId: { type: Number, default: null },
  canManageMessages: { type: Boolean, default: false },
  canEditOwnMessages: { type: Boolean, default: true },
});
const emit = defineEmits(['delete']);
const { t } = useI18n();
const editingMessageId = ref(null);

const senderName = message =>
  message.sender?.display_name ||
  message.sender?.name ||
  t('INTERNAL_CHAT.UNKNOWN_SENDER');

const startEdit = message => {
  editingMessageId.value = message.id;
};

const cancelEdit = () => {
  editingMessageId.value = null;
};
</script>

<template>
  <article
    :data-message-id="message.id"
    class="group flex gap-2 text-sm"
    :class="message.sender_id === currentUserId ? 'flex-row-reverse' : ''"
  >
    <Avatar :name="senderName(message)" :size="24" />
    <div
      class="min-w-0 flex-1"
      :class="message.sender_id === currentUserId ? 'text-end' : ''"
    >
      <p class="mb-1 text-xs text-n-slate-11">
        {{ senderName(message) }}
      </p>
      <Bubble
        :variant="
          message.sender_id === currentUserId
            ? MESSAGE_VARIANTS.AGENT
            : MESSAGE_VARIANTS.USER
        "
        :orientation="
          message.sender_id === currentUserId
            ? ORIENTATION.RIGHT
            : ORIENTATION.LEFT
        "
        class="w-fit max-w-full px-4 py-3"
        :class="message.sender_id === currentUserId ? 'ms-auto' : ''"
      >
        <InternalChatMessageContent :message="message">
          <template v-if="editingMessageId === message.id" #editor>
            <InternalChatEditForm
              :key="message.id"
              :message="message"
              :save-message="saveMessage"
              :members="members"
              :can-mention-all="canMentionAll"
              @close="cancelEdit"
            />
          </template>
        </InternalChatMessageContent>
      </Bubble>
      <div
        v-if="!message.deleted && message.reactions?.length"
        class="mt-2 flex flex-wrap gap-1"
        data-testid="internal-chat-thread-reactions"
      >
        <span
          v-for="reaction in message.reactions"
          :key="reaction.emoji"
          class="rounded-full bg-n-alpha-3 px-2 py-1 text-xs"
          :aria-label="t('INTERNAL_CHAT.REACT_WITH', { emoji: reaction.emoji })"
        >
          {{ reaction.emoji }} {{ reaction.count }}
        </span>
      </div>
      <InternalChatMessageActions
        :message="message"
        :current-user-id="currentUserId"
        :can-manage-messages="canManageMessages"
        :can-edit-own-messages="canEditOwnMessages"
        :show-reply="false"
        :disabled="editingMessageId === message.id"
        @edit="startEdit"
        @delete="emit('delete', $event)"
      />
    </div>
  </article>
</template>
